import React, { useMemo } from 'react';
import { computeWeeklyBuckets, computeFunnel, computeSparkline } from './AnalyticsUtils';

// ─── Inline SVG icons ─────────────────────────────────────────────────────────
const SendIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
  </svg>
);
const MailOpenIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 5H3a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z"/>
    <polyline points="1 5 12 13 23 5"/>
  </svg>
);
const UsersIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const AwardIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="6"/>
    <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"/>
  </svg>
);
const TrendUpIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>
  </svg>
);
const TrendDownIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/>
  </svg>
);

// ─── Sparkline SVG ────────────────────────────────────────────────────────────
export function Sparkline({ data, color = '#004D40', fill = 'rgba(0,77,64,.10)' }) {
  if (!data || data.length < 2) return null;
  const w = 90, h = 28;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = Math.max(max - min, 1);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * (w - 2) + 1;
    const y = h - 2 - ((v - min) / range) * (h - 6);
    return [x, y];
  });
  const line = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const area = `${line} L${w - 1},${h} L1,${h} Z`;
  return (
    <svg className="an-kpi-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <path d={area} fill={fill} stroke="none" />
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Trend vs previous 4-week period ─────────────────────────────────────────
function trendVsPrev(stellen, filterFn) {
  const MS4W = 28 * 86400000;
  const now = Date.now();
  const curr = stellen.filter(s => {
    const d = new Date(s.erstellt_am || 0).getTime();
    return filterFn(s) && d > now - MS4W;
  }).length;
  const prev = stellen.filter(s => {
    const d = new Date(s.erstellt_am || 0).getTime();
    return filterFn(s) && d <= now - MS4W && d > now - 2 * MS4W;
  }).length;
  if (prev === 0) return curr > 0 ? 100 : 0;
  return Math.round(((curr - prev) / prev) * 100);
}

// ─── KPI Strip ────────────────────────────────────────────────────────────────
export function KpiStrip({ stellen = [] }) {
  const gesamt    = stellen.length;
  const interview = useMemo(() => stellen.filter(s => s.bewerbungs_status === 'kennenlerngespraech').length, [stellen]);
  const zusage    = useMemo(() => stellen.filter(s => s.bewerbungs_status === 'zusage').length, [stellen]);
  const antwortquote = gesamt > 0 ? Math.round(((interview + zusage) / gesamt) * 100) : 0;

  const spark      = useMemo(() => computeSparkline(stellen, 12), [stellen]);
  const sparkResp  = useMemo(() => computeSparkline(stellen.filter(s => ['kennenlerngespraech','zusage','absage'].includes(s.bewerbungs_status)), 12), [stellen]);
  const sparkInt   = useMemo(() => computeSparkline(stellen.filter(s => s.bewerbungs_status === 'kennenlerngespraech'), 12), [stellen]);
  const sparkOffer = useMemo(() => computeSparkline(stellen.filter(s => s.bewerbungs_status === 'zusage'), 12), [stellen]);

  const trendGesamt = useMemo(() => trendVsPrev(stellen, () => true), [stellen]);
  const trendInt    = useMemo(() => trendVsPrev(stellen, s => s.bewerbungs_status === 'kennenlerngespraech'), [stellen]);
  const trendZusage = useMemo(() => trendVsPrev(stellen, s => s.bewerbungs_status === 'zusage'), [stellen]);

  const kpis = [
    {
      label: 'Bewerbungen gesamt',
      value: gesamt,
      unit: null,
      Icon: SendIcon,
      iconCls: '',
      trend: trendGesamt,
      spark,
      sparkColor: '#004D40',
      sparkFill: 'rgba(0,77,64,.12)',
    },
    {
      label: 'Antwortquote',
      value: antwortquote,
      unit: '%',
      Icon: MailOpenIcon,
      iconCls: 'amber',
      trend: null,
      spark: sparkResp,
      sparkColor: '#A67800',
      sparkFill: 'rgba(255,188,0,.18)',
    },
    {
      label: 'Interviews',
      value: interview,
      unit: null,
      Icon: UsersIcon,
      iconCls: 'green',
      trend: trendInt,
      spark: sparkInt,
      sparkColor: '#00806A',
      sparkFill: 'rgba(0,160,120,.14)',
    },
    {
      label: 'Zusagen',
      value: zusage,
      unit: null,
      Icon: AwardIcon,
      iconCls: 'green',
      trend: trendZusage,
      spark: sparkOffer,
      sparkColor: '#00806A',
      sparkFill: 'rgba(0,160,120,.14)',
    },
  ];

  return (
    <div id="tutorial-an-kpi" className="an-kpi-grid">
      {kpis.map(k => {
        const up = k.trend !== null && k.trend > 0;
        const down = k.trend !== null && k.trend < 0;
        return (
          <div key={k.label} className="an-kpi">
            <div className="an-kpi-head">
              <span className="an-kpi-label">{k.label}</span>
              <span className={`an-kpi-icon-box${k.iconCls ? ` an-kpi-icon-box--${k.iconCls}` : ''}`}>
                <k.Icon />
              </span>
            </div>
            <div className="an-kpi-value">
              {k.value}
              {k.unit && <span className="an-kpi-unit">{k.unit}</span>}
            </div>
            <div className="an-kpi-foot">
              <span>
                {k.trend !== null ? (
                  <>
                    <span className={`an-kpi-trend${up ? ' an-kpi-trend--up' : down ? ' an-kpi-trend--down' : ''}`}>
                      {up ? <TrendUpIcon /> : down ? <TrendDownIcon /> : null}
                      {Math.abs(k.trend)}%
                    </span>
                    {' '}
                    <span className="an-kpi-since">vs. Vorperiode</span>
                  </>
                ) : (
                  <span className="an-kpi-since">Gesamt</span>
                )}
              </span>
              <Sparkline data={k.spark} color={k.sparkColor} fill={k.sparkFill} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Funnel ───────────────────────────────────────────────────────────────────
export function Funnel({ stellen = [] }) {
  const { sent, interview, zusage, absage } = useMemo(() => computeFunnel(stellen), [stellen]);
  const base = sent || 1;
  const steps = [
    { key: 'sent',    n: sent,      label: 'Abgeschickt', mod: 'sent'   },
    { key: 'seen',    n: 0,         label: 'Gesehen',     mod: 'seen'   },
    { key: 'int',     n: interview, label: 'Interview',   mod: 'int'    },
    { key: 'offer',   n: zusage,    label: 'Zusage',      mod: 'offer'  },
    { key: 'reject',  n: absage,    label: 'Absage',      mod: 'reject' },
  ];
  return (
    <div className="an-card">
      <div className="an-card-head">
        <div>
          <div className="an-card-eyebrow">Bewerbungs-Funnel</div>
          <h3 className="an-card-title">Vom <em>Klick</em> zur Zusage</h3>
        </div>
      </div>
      <div className="an-funnel">
        {steps.map((s, i) => {
          const pctVal = s.n > 0 ? Math.round((s.n / base) * 100) : 0;
          return (
            <div key={s.key} className={`an-funnel-step an-funnel-step--${s.mod}${i === 0 ? ' an-funnel-step--first' : ''}${i === steps.length - 1 ? ' an-funnel-step--last' : ''}`}>
              <div className="an-funnel-num">{s.n}</div>
              <div className="an-funnel-lbl">{s.label}</div>
              <div className="an-funnel-pct">{pctVal}% der Vers.</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Bar Chart ────────────────────────────────────────────────────────────────
export function BarChart({ stellen = [] }) {
  const weeks = useMemo(() => computeWeeklyBuckets(stellen, 12), [stellen]);
  const max = Math.max(...weeks.map(w => w.sent + w.interview + w.zusage), 1);
  const pctH = (n) => `${(n / max) * 100}%`;

  const total = weeks.reduce((s, w) => s + w.sent + w.interview + w.zusage, 0);
  const avgPerWeek = (total / 12).toFixed(1).replace('.', ',');
  const peakIdx = weeks.reduce((best, w, i) => {
    const t = w.sent + w.interview + w.zusage;
    const bestT = weeks[best].sent + weeks[best].interview + weeks[best].zusage;
    return t > bestT ? i : best;
  }, 0);
  const peakLabel = weeks[peakIdx]?.label || '';

  const half1 = weeks.slice(0, 6).reduce((s, w) => s + w.sent + w.interview + w.zusage, 0);
  const half2 = weeks.slice(6).reduce((s, w) => s + w.sent + w.interview + w.zusage, 0);
  const delta = half1 > 0 ? Math.round(((half2 - half1) / half1) * 100) : 0;

  return (
    <div className="an-card">
      <div className="an-card-head">
        <div>
          <div className="an-card-eyebrow">Aktivität · 12 Wochen</div>
          <h3 className="an-card-title">Bewerbungen pro Woche</h3>
        </div>
      </div>
      <div className="an-chart-wrap">
        <div className="an-chart-summary">
          <span className="an-chart-summary-num">{total}</span>
          <span className="an-chart-summary-delta">
            <TrendUpIcon />
            {delta >= 0 ? '+' : ''}{delta}% vs. Vorperiode
          </span>
          <span className="an-chart-summary-ctx">Ø {avgPerWeek} / Woche · Peak {peakLabel}</span>
        </div>
        <div className="an-bars">
          {weeks.map(w => {
            const tot = w.sent + w.interview + w.zusage;
            return (
              <div key={w.label} className="an-bar-col">
                <div className="an-bar-stack" style={{ height: pctH(tot) }}>
                  {w.zusage    > 0 && <div className="an-bar-seg an-bar-seg--offer"     style={{ flexBasis: pctH(w.zusage) }} />}
                  {w.interview > 0 && <div className="an-bar-seg an-bar-seg--interview" style={{ flexBasis: pctH(w.interview) }} />}
                  {w.sent      > 0 && <div className="an-bar-seg an-bar-seg--sent"      style={{ flexBasis: pctH(w.sent) }} />}
                </div>
                <div className="an-bar-lbl">{w.label.replace('KW', '')}</div>
              </div>
            );
          })}
        </div>
        <div className="an-chart-legend">
          <span><span className="an-legend-dot an-legend-dot--sent" />Abgeschickt</span>
          <span><span className="an-legend-dot an-legend-dot--interview" />Interview</span>
          <span><span className="an-legend-dot an-legend-dot--offer" />Zusage</span>
        </div>
      </div>
    </div>
  );
}
